// Menu Vendas → Pré-contratos (pedido 2026-10-05). Todo envio de formulário
// (Administração → Formulários) aparece aqui como pré-cadastro — nem todo
// mundo que preenche fecha negócio. Fluxo: completar/editar o cadastro →
// Criar usuário (cliente, mesmo dialog de Usuários já pré-preenchido) →
// Gerar contrato (modelo + valores + veículos digitados; o contrato nunca
// depende de rastreador vinculado). Acesso: admin ou permissão "vendas"
// (funcionário/vendedor); cliente nunca — o backend aplica a mesma regra.
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import {
  CheckCircle2,
  ChevronDown,
  Eye,
  Copy,
  Download,
  ExternalLink,
  FileSignature,
  FileSpreadsheet,
  Inbox,
  Link2,
  Loader2,
  Lock,
  MessageCircle,
  Plus,
  Search,
  Trash2,
  UserPlus,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { GpsInlineLoader } from "@/components/gps-loader";
import { GpsDriveApiError } from "@/lib/api/gpsdrive-client";
import { SALES_LEADS_KEY, useSalesAccess, useSalesLeads, useSalesSheet } from "@/hooks/queries/sales";
import { SalesSheetDialog } from "@/components/sales/sales-sheet-dialog";
import {
  blankLeadData,
  salesService,
  STAGE_LABELS,
  type LeadData,
  type LeadStage,
  type LeadStatus,
  type SalesAccess,
  type SalesLead,
  type SheetImportResult,
} from "@/services/gpsdrive-sales.service";
import {
  useClientContract,
  useClientProfile,
  useContractSignatories,
  useContractTemplates,
  useGenerateContractSigningLink,
  useSendSigningLinkByWhatsapp,
  useUpdateClientContract,
  useDeleteClientContract,
  useWhatsappIntegration,
  useDevicePermissions,
  useLinkableUsers,
  usePlans,
  useClientContractVersions,
  queryKeys,
} from "@/hooks/queries";
import { gpsDriveClientContractService, type ClientContractVersion } from "@/services/gpsdrive-client-contract.service";
import { ContractVehiclesEditor, vehiclesMonthlyTotal } from "@/components/contract-vehicles-editor";
import { gpsDriveUsersService } from "@/services/gpsdrive-users.service";
import type { ContractVehicle } from "@/services/gpsdrive-client-contract.service";
import { clientProfileToCliState } from "@/lib/user-client-profile";
import { ClientContractDocument, ClientContractPrintReport, printClientContract } from "@/components/client-contract-print-report";
import { CreateUserDialog, type CreateUserPrefill } from "@/routes/users";
import { ContractSignatoryPicker, contractSignatoryIds, defaultSignatoryIds } from "@/components/contract-signatory-picker";
import type { ClientContract } from "@/services/gpsdrive-client-contract.service";

export const Route = createFileRoute("/vendas")({
  component: VendasGate,
  head: () => ({ meta: [{ title: "Vendas — GPS Drive" }] }),
});

function VendasGate() {
  const { data: access, isLoading } = useSalesAccess();
  if (isLoading) return <GpsInlineLoader className="h-[70vh]" />;
  if (!access?.view) {
    return (
      <div className="flex h-screen items-center justify-center bg-background p-6">
        <div className="max-w-md text-center">
          <Lock className="mx-auto h-8 w-8 text-muted-foreground" />
          <h1 className="mt-3 text-lg font-semibold">Sem permissão</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Você não tem acesso a Vendas. Peça a um administrador para liberar em Usuários → Editar → Permissões →
            Vendas.
          </p>
        </div>
      </div>
    );
  }
  return <VendasPage access={access} />;
}

// ---------------------------------------------------------------------------
// Utilidades

const STAGE_ORDER: LeadStage[] = ["novo", "em_contato", "usuario_criado", "contrato_enviado", "assinado", "perdido"];

const STAGE_STYLES: Record<LeadStage, string> = {
  novo: "border-sky-500/30 bg-sky-500/10 text-sky-700 dark:text-sky-300",
  em_contato: "border-violet-500/30 bg-violet-500/10 text-violet-700 dark:text-violet-300",
  usuario_criado: "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300",
  contrato_enviado: "border-orange-500/30 bg-orange-500/10 text-orange-700 dark:text-orange-300",
  assinado: "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  perdido: "border-slate-400/30 bg-slate-400/10 text-slate-600 dark:text-slate-400",
};

function StageBadge({ stage }: { stage: LeadStage }) {
  return (
    <span
      className={cn(
        "inline-flex items-center whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] font-semibold",
        STAGE_STYLES[stage],
      )}
    >
      {STAGE_LABELS[stage]}
    </span>
  );
}

const fmtDate = (iso: string) => format(new Date(iso), "dd/MM/yyyy HH:mm");
const digits = (v: string) => v.replace(/\D/g, "");
const brlInput = (n: number | null) => (n == null ? "" : n.toFixed(2).replace(".", ","));
const parseBrl = (s: string): number | null => {
  const t = s.trim();
  if (!t) return null;
  const n = Number(t.replace(/\./g, "").replace(",", "."));
  return Number.isFinite(n) && n >= 0 ? n : null;
};

// Plano do formulário vem como "Plano Pró — R$ 69,90"; no contrato só o nome.
const planName = (plan: string) => plan.split(/\s+[—-]\s+R\$/)[0]?.trim() ?? plan;

function vehicleSummary(d: LeadData): string {
  const v = d.vehicles[0];
  if (!v) return "—";
  const main = [v.plate, v.model].filter(Boolean).join(" · ") || "—";
  return d.vehicles.length > 1 ? `${main} +${d.vehicles.length - 1}` : main;
}

// Planos dos veículos (cada um tem o seu); sem nenhum, o do formulário.
function leadPlansSummary(d: LeadData): string {
  const plans = [...new Set(d.vehicles.map((v) => v.plan).filter(Boolean))];
  if (plans.length) return plans.length > 2 ? `${plans.slice(0, 2).join(", ")} +${plans.length - 2}` : plans.join(", ");
  return d.plan ? planName(d.plan) : "—";
}

function leadToPrefill(d: LeadData): CreateUserPrefill {
  const notes = [
    d.authorizedPeople.some(Boolean) ? `Pessoas autorizadas: ${d.authorizedPeople.filter(Boolean).join(", ")}` : "",
    d.plan ? `Plano escolhido: ${d.plan}` : "",
    d.paymentMethod ? `Forma de pagamento: ${d.paymentMethod}` : "",
    d.vehicles.length
      ? `Veículos: ${d.vehicles
          .map((v) =>
            [v.plate, v.model, v.year, v.color, v.plan, v.monthlyValue != null ? `R$ ${brlInput(v.monthlyValue)}` : ""]
              .filter(Boolean)
              .join(" "),
          )
          .join("; ")}`
      : "",
    d.notes,
  ]
    .filter(Boolean)
    .join("\n");
  return {
    nome: d.name,
    // Login não precisa ser e-mail (importação já usa assim); sem e-mail
    // no cadastro, cai pro CPF/CNPJ só com dígitos.
    loginEmail: d.email || digits(d.cpfCnpj),
    telefone: d.phone1,
    cli: {
      cpfCnpj: d.cpfCnpj,
      nomeContrato: d.name,
      rg: d.rg,
      dataNascimento: d.birthDate,
      cep: d.cep,
      endereco: d.street,
      numero: d.number,
      complemento: d.complement,
      bairro: d.neighborhood,
      cidade: d.city,
      estado: d.state,
      celular: d.phone1,
      foneFixo: d.phone2,
      diaVencimento: d.dueDay ? String(Number(d.dueDay)) : "",
      // Soma das mensalidades dos veículos; sem valores, a do plano.
      valorMensalidade: brlInput(vehiclesMonthlyTotal(d.vehicles) ?? d.planValue),
      emailCobranca: d.email,
      notas: notes,
    },
  };
}

function errMsg(err: unknown, fallback: string) {
  return err instanceof GpsDriveApiError ? err.message : fallback;
}

// ---------------------------------------------------------------------------
// Página

type Filter = "todos" | LeadStage;

function VendasPage({ access }: { access: SalesAccess }) {
  const { data: leads, isLoading, isError } = useSalesLeads();
  const [filter, setFilter] = useState<Filter>("todos");
  const [query, setQuery] = useState("");
  const [openId, setOpenId] = useState<string | null>(null);
  const [creatingManual, setCreatingManual] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [newContractOpen, setNewContractOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [contractLeadId, setContractLeadId] = useState<string | null>(null);
  const { data: sheet } = useSalesSheet(access.edit);
  const qc = useQueryClient();

  const counts = useMemo(() => {
    const c: Record<string, number> = { todos: 0 };
    for (const l of leads ?? []) {
      c[l.stage] = (c[l.stage] ?? 0) + 1;
      if (l.stage !== "perdido") c.todos += 1;
    }
    return c;
  }, [leads]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    const qd = digits(q);
    return (leads ?? []).filter((l) => {
      if (filter === "todos" ? l.stage === "perdido" : l.stage !== filter) return false;
      if (!q) return true;
      const d = l.data;
      const hay = [d.name, d.email, d.city, d.plan, ...d.vehicles.map((v) => `${v.plate} ${v.model}`)]
        .join(" ")
        .toLowerCase();
      if (hay.includes(q)) return true;
      return !!qd && [d.cpfCnpj, d.phone1, d.phone2].some((v) => digits(v).includes(qd));
    });
  }, [leads, filter, query]);

  const openLead = leads?.find((l) => l.id === openId) ?? null;
  const contractLead = leads?.find((l) => l.id === contractLeadId) ?? null;

  const createManual = async () => {
    setCreatingManual(true);
    try {
      const created = await salesService.create(blankLeadData());
      await qc.invalidateQueries({ queryKey: SALES_LEADS_KEY });
      setOpenId(created.id);
    } catch (err) {
      toast.error("Não foi possível criar o pré-contrato", { description: errMsg(err, "Tente de novo.") });
    } finally {
      setCreatingManual(false);
    }
  };

  return (
    <div className="min-h-full bg-muted/30">
      <div className="mx-auto max-w-[1400px] space-y-5 p-4 md:p-6">
        <header className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight">
              <span className="grid h-9 w-9 place-items-center rounded-xl bg-orange-500/15 text-lg">🛒</span>
              Vendas
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Pré-contratos vindos dos formulários — complete o cadastro, crie o cliente e gere o contrato
            </p>
          </div>
          {access.edit && (
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" onClick={() => setSheetOpen(true)}>
                <FileSpreadsheet className="mr-2 h-4 w-4 text-emerald-600" />
                Planilha
                {sheet?.url && (
                  <span
                    className={cn(
                      "ml-2 h-2 w-2 rounded-full",
                      sheet.lastError ? "bg-destructive" : sheet.enabled ? "bg-emerald-500" : "bg-slate-400",
                    )}
                  />
                )}
              </Button>
              {sheet?.url && sheet.sheetName && (
                <Button variant="outline" onClick={() => setImportOpen(true)}>
                  <Download className="mr-2 h-4 w-4 text-emerald-600" />
                  Puxar da planilha
                </Button>
              )}
              <Button variant="outline" onClick={createManual} disabled={creatingManual}>
                {creatingManual ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
                Novo pré-contrato
              </Button>
              <Button onClick={() => setNewContractOpen(true)}>
                <FileSignature className="mr-2 h-4 w-4" />
                Gerar contrato
              </Button>
            </div>
          )}
        </header>

        <div className="flex flex-wrap items-center gap-2">
          {(["todos", ...STAGE_ORDER] as Filter[]).map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setFilter(f)}
              className={cn(
                "flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors",
                filter === f
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border bg-card text-muted-foreground hover:text-foreground",
              )}
            >
              {f === "todos" ? "Em andamento" : STAGE_LABELS[f]}
              <span
                className={cn(
                  "rounded-full px-1.5 text-[10px] font-bold",
                  filter === f ? "bg-primary-foreground/20" : "bg-muted",
                )}
              >
                {counts[f] ?? 0}
              </span>
            </button>
          ))}
          <div className="relative ml-auto w-full sm:w-72">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Nome, CPF, telefone, placa…"
              className="pl-9"
            />
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-border bg-card">
          {isLoading ? (
            <GpsInlineLoader className="h-64" />
          ) : isError ? (
            <div className="p-10 text-center text-sm text-muted-foreground">Não foi possível carregar os pré-contratos.</div>
          ) : visible.length === 0 ? (
            <div className="flex flex-col items-center gap-2 p-12 text-center text-sm text-muted-foreground">
              <Inbox className="h-8 w-8" />
              {leads?.length
                ? "Nenhum pré-contrato neste filtro."
                : "Nenhum cadastro ainda. Tudo que for enviado pelos formulários aparece aqui."}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="px-4 py-2.5 font-semibold">Cliente</th>
                    <th className="px-4 py-2.5 font-semibold">Contato</th>
                    <th className="px-4 py-2.5 font-semibold">Veículo</th>
                    <th className="px-4 py-2.5 font-semibold">Plano</th>
                    <th className="px-4 py-2.5 font-semibold">Recebido</th>
                    <th className="px-4 py-2.5 font-semibold">Etapa</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((l) => (
                    <tr
                      key={l.id}
                      onClick={() => setOpenId(l.id)}
                      className="cursor-pointer border-t border-border transition-colors hover:bg-muted/40"
                    >
                      <td className="px-4 py-3">
                        <div className="font-medium">{l.data.name || "(sem nome)"}</div>
                        <div className="text-xs text-muted-foreground">{l.data.cpfCnpj || l.data.email || "—"}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div>{l.data.phone1 || "—"}</div>
                        <div className="text-xs text-muted-foreground">{l.data.city}</div>
                      </td>
                      <td className="px-4 py-3">{vehicleSummary(l.data)}</td>
                      <td className="px-4 py-3">{leadPlansSummary(l.data)}</td>
                      <td className="px-4 py-3">
                        <div>{fmtDate(l.createdAt)}</div>
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          {l.source === "manual" ? "Cadastro manual" : l.source === "sheet" ? "Planilha" : (l.formTitle ?? "Formulário")}
                          {l.sheetSentAt && (
                            <FileSpreadsheet
                              className="h-3.5 w-3.5 text-emerald-600"
                              aria-label={`Na planilha desde ${fmtDate(l.sheetSentAt)}`}
                            />
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <StageBadge stage={l.stage} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {openLead && (
        <LeadDialog
          key={openLead.id}
          lead={openLead}
          access={access}
          sheetReady={!!sheet?.url && !!sheet.sheetName && sheet.mapping.length > 0}
          onClose={() => setOpenId(null)}
        />
      )}
      {sheetOpen && <SalesSheetDialog leads={leads ?? []} onClose={() => setSheetOpen(false)} />}
      {importOpen && <SheetImportDialog sheetName={sheet?.sheetName ?? ""} onClose={() => setImportOpen(false)} />}
      {newContractOpen && (
        <NewContractDialog
          leads={leads ?? []}
          onClose={() => setNewContractOpen(false)}
          onReady={(leadId) => {
            setNewContractOpen(false);
            setContractLeadId(leadId);
          }}
        />
      )}
      {contractLead?.user && (
        <ContractDialog
          key={contractLead.id}
          lead={contractLead}
          leadData={contractLead.data}
          userId={contractLead.user.id}
          onClose={() => setContractLeadId(null)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Puxar da planilha (pedido 2026-10-15): a planilha tem uma linha por veículo
// e o cliente repetido. O servidor junta por CPF/CNPJ ou nome; cada veículo
// leva o plano e a mensalidade da sua linha. Cliente que já está em Vendas só
// ganha os veículos novos e os campos vazios. Primeiro mostra o que vai
// acontecer (dryRun), depois importa.

function SheetImportDialog({ sheetName, onClose }: { sheetName: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [preview, setPreview] = useState<SheetImportResult | null>(null);
  const [done, setDone] = useState<SheetImportResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    salesService
      .importFromSheet(true)
      .then(setPreview)
      .catch((err) => setError(errMsg(err, "Não foi possível ler a planilha.")))
      .finally(() => setLoading(false));
  }, []);

  const run = async () => {
    setImporting(true);
    setError(null);
    try {
      const res = await salesService.importFromSheet(false);
      setDone(res);
      await qc.invalidateQueries({ queryKey: SALES_LEADS_KEY });
    } catch (err) {
      setError(errMsg(err, "Não foi possível importar."));
    } finally {
      setImporting(false);
    }
  };

  const r = done ?? preview;
  return (
    <Dialog open onOpenChange={(o) => !o && !importing && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader className="text-left">
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-4 w-4 text-emerald-600" /> Puxar da planilha
          </DialogTitle>
          <DialogDescription>
            Aba “{sheetName}”. Linhas do mesmo cliente viram um cadastro só, com um veículo por linha (plano e mensalidade
            de cada um).
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <GpsInlineLoader className="h-32" />
        ) : r ? (
          <div className="space-y-3">
            {done && (
              <div className="flex items-center gap-2 rounded-lg border border-emerald-600/30 bg-emerald-600/10 px-3 py-2 text-sm font-medium text-emerald-700 dark:text-emerald-400">
                <CheckCircle2 className="h-4 w-4" /> Importação concluída
              </div>
            )}
            <div className="grid grid-cols-2 gap-2 text-sm">
              {[
                ["Linhas na planilha", r.rows],
                ["Clientes (sem repetir)", r.clients],
                [done ? "Cadastros criados" : "Cadastros novos", r.created],
                [done ? "Cadastros completados" : "Já em Vendas, a completar", r.updated],
                ["Já iguais (nada muda)", r.unchanged],
                [done ? "Veículos adicionados" : "Veículos a adicionar", r.vehiclesAdded],
              ].map(([label, value]) => (
                <div key={String(label)} className="rounded-lg border border-border px-3 py-2">
                  <div className="text-[11px] text-muted-foreground">{label}</div>
                  <div className="text-lg font-semibold">{value}</div>
                </div>
              ))}
            </div>
            {!done && (
              <p className="text-xs text-muted-foreground">
                Nada é apagado. Os importados não voltam para a planilha.
              </p>
            )}
          </div>
        ) : null}
        {error && <p className="text-sm text-destructive">{error}</p>}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={importing}>
            {done ? "Fechar" : "Cancelar"}
          </Button>
          {!done && preview && (
            <Button onClick={run} disabled={importing || (preview.created === 0 && preview.updated === 0)}>
              {importing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Importar
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Gerar contrato direto (pedido 2026-10-15): botão no topo de Vendas, sem
// passar por um formulário. O contrato é do usuário (client_contracts), então
// primeiro o cliente — novo (mesmo CreateUserDialog de Usuários) ou já
// cadastrado — e só depois o ContractDialog de sempre (modelo, valores,
// veículo cadastrado ou digitado, link). Fica um pré-contrato "manual" ligado
// ao cliente pra acompanhar a etapa na lista; cliente que já tem um reaproveita.
// O PDF fica no cliente: Usuários → seta → Contratos.

function NewContractDialog({
  leads,
  onClose,
  onReady,
}: {
  leads: SalesLead[];
  onClose: () => void;
  onReady: (leadId: string) => void;
}) {
  const qc = useQueryClient();
  const [mode, setMode] = useState<"choose" | "existing">("choose");
  const [createOpen, setCreateOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [busyId, setBusyId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { data: users, isLoading } = useLinkableUsers(mode === "existing");

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = (users ?? []).filter((u) => u.status === "minha_empresa");
    const hits = q ? list.filter((u) => `${u.name} ${u.email}`.toLowerCase().includes(q)) : list;
    return hits.slice(0, 50);
  }, [users, query]);

  const prepare = async (user: { id: number; name: string; email: string; phone?: string }) => {
    setBusyId(user.id);
    setError(null);
    try {
      const existing = leads.find((l) => l.user?.traccarUserId === user.id);
      if (existing) {
        onReady(existing.id);
        return;
      }
      // Cliente recém-criado pode levar um instante pra existir no GPS Drive.
      await gpsDriveUsersService.resolveByTraccarId(user.id);
      const lead = await salesService.create({
        ...blankLeadData(),
        name: user.name,
        email: user.email.includes("@") ? user.email : "",
        phone1: user.phone ?? "",
      });
      await salesService.linkUser(lead.id, user.id);
      await qc.invalidateQueries({ queryKey: SALES_LEADS_KEY });
      onReady(lead.id);
    } catch (err) {
      setError(errMsg(err, "Não foi possível preparar o contrato. Tente de novo."));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      <Dialog open={!createOpen} onOpenChange={(o) => !o && !createOpen && onClose()}>
        <DialogContent className="max-h-[92vh] max-w-lg overflow-y-auto">
          <DialogHeader className="text-left">
            <DialogTitle className="flex items-center gap-2">
              <FileSignature className="h-4 w-4" /> Gerar contrato
            </DialogTitle>
            <DialogDescription>
              O contrato fica no cadastro do cliente — primeiro crie o usuário ou escolha um já cadastrado.
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-2 sm:flex-row">
            <Step n={1} title="Cliente" done={false} active>
              Criar ou escolher
            </Step>
            <Step n={2} title="Contrato" done={false} active={false}>
              Modelo, valores, veículo, link
            </Step>
          </div>

          {mode === "choose" ? (
            <div className="grid gap-2 sm:grid-cols-2">
              <button
                type="button"
                onClick={() => setCreateOpen(true)}
                className="flex flex-col items-start gap-2 rounded-xl border border-primary/40 bg-primary/5 p-4 text-left transition-colors hover:bg-primary/10"
              >
                <span className="grid h-9 w-9 place-items-center rounded-lg bg-primary text-primary-foreground">
                  <UserPlus className="h-4 w-4" />
                </span>
                <span className="text-sm font-semibold">Novo cliente</span>
                <span className="text-xs text-muted-foreground">Cria o usuário e já segue pro contrato</span>
              </button>
              <button
                type="button"
                onClick={() => setMode("existing")}
                className="flex flex-col items-start gap-2 rounded-xl border border-border p-4 text-left transition-colors hover:border-primary/40 hover:bg-muted/60"
              >
                <span className="grid h-9 w-9 place-items-center rounded-lg bg-muted text-foreground">
                  <Search className="h-4 w-4" />
                </span>
                <span className="text-sm font-semibold">Cliente já cadastrado</span>
                <span className="text-xs text-muted-foreground">Busca pelo nome ou login</span>
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    autoFocus
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Nome ou login do cliente…"
                    className="pl-9"
                  />
                </div>
                <Button variant="ghost" size="sm" onClick={() => setMode("choose")}>
                  Voltar
                </Button>
              </div>
              <div className="max-h-80 overflow-y-auto rounded-lg border border-border">
                {isLoading ? (
                  <GpsInlineLoader className="h-32" />
                ) : results.length === 0 ? (
                  <div className="p-6 text-center text-sm text-muted-foreground">Nenhum cliente encontrado.</div>
                ) : (
                  results.map((u) => (
                    <button
                      key={u.id}
                      type="button"
                      disabled={busyId !== null}
                      onClick={() => prepare(u)}
                      className="flex w-full items-center gap-3 border-b border-border px-3 py-2.5 text-left last:border-b-0 hover:bg-muted/60 disabled:opacity-60"
                    >
                      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-muted text-xs font-bold">
                        {u.name.trim().charAt(0).toUpperCase() || "?"}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium">{u.name}</span>
                        <span className="block truncate text-xs text-muted-foreground">{u.email}</span>
                      </span>
                      {busyId === u.id ? (
                        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                      ) : leads.some((l) => l.user?.traccarUserId === u.id && l.contract?.signingToken) ? (
                        <span className="shrink-0 text-[11px] text-muted-foreground">tem contrato</span>
                      ) : null}
                    </button>
                  ))
                )}
              </div>
            </div>
          )}

          {busyId !== null && mode === "choose" && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Preparando o contrato…
            </div>
          )}
          {error && <div className="text-xs text-destructive">{error}</div>}
        </DialogContent>
      </Dialog>

      {createOpen && (
        <CreateUserDialog
          open={createOpen}
          onOpenChange={setCreateOpen}
          onCreated={(user) => {
            setCreateOpen(false);
            void prepare(user);
          }}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Detalhe do pré-contrato

function Field({
  label,
  value,
  onChange,
  disabled,
  className,
  placeholder,
  type = "text",
  onBlur,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  className?: string;
  placeholder?: string;
  type?: string;
  onBlur?: () => void;
}) {
  return (
    <label className={cn("block", className)}>
      <span className="mb-1 block text-xs font-medium text-muted-foreground">{label}</span>
      <Input
        type={type}
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        onBlur={onBlur}
        onChange={(e) => onChange(e.target.value)}
        className="h-9"
      />
    </label>
  );
}

function Section({ title, children, right }: { title: string; children: ReactNode; right?: ReactNode }) {
  return (
    <section className="rounded-lg border border-border p-4">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-xs font-bold uppercase tracking-wide text-muted-foreground">{title}</h3>
        {right}
      </div>
      {children}
    </section>
  );
}

function Step({ n, title, done, active, children }: { n: number; title: string; done: boolean; active: boolean; children?: ReactNode }) {
  return (
    <div
      className={cn(
        "flex flex-1 items-center gap-3 rounded-lg border px-3 py-2.5",
        done ? "border-emerald-500/30 bg-emerald-500/5" : active ? "border-primary/40 bg-primary/5" : "border-border",
      )}
    >
      <span
        className={cn(
          "grid h-7 w-7 shrink-0 place-items-center rounded-full text-xs font-bold",
          done ? "bg-emerald-500 text-white" : active ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground",
        )}
      >
        {done ? <CheckCircle2 className="h-4 w-4" /> : n}
      </span>
      <div className="min-w-0">
        <div className="text-sm font-semibold">{title}</div>
        {children && <div className="truncate text-xs text-muted-foreground">{children}</div>}
      </div>
    </div>
  );
}

function LeadDialog({
  lead,
  access,
  sheetReady,
  onClose,
}: {
  lead: SalesLead;
  access: SalesAccess;
  sheetReady: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { data: plans } = usePlans();
  const [data, setData] = useState<LeadData>(lead.data);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showAnswers, setShowAnswers] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [createUserOpen, setCreateUserOpen] = useState(false);
  const [prefill, setPrefill] = useState<CreateUserPrefill | null>(null);
  const [contractOpen, setContractOpen] = useState(false);
  const [sendingSheet, setSendingSheet] = useState(false);
  const readOnly = !access.edit;
  const dirty = JSON.stringify(data) !== JSON.stringify(lead.data);
  const set = <K extends keyof LeadData>(k: K, v: LeadData[K]) => setData((p) => ({ ...p, [k]: v }));

  const refresh = () => qc.invalidateQueries({ queryKey: SALES_LEADS_KEY });

  const save = async (): Promise<boolean> => {
    if (!dirty) return true;
    setSaving(true);
    setSaveError(null);
    try {
      await salesService.update(lead.id, { data });
      await refresh();
      return true;
    } catch (err) {
      setSaveError(errMsg(err, "Não foi possível salvar."));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (status: LeadStatus) => {
    try {
      await salesService.update(lead.id, { status });
      await refresh();
    } catch (err) {
      toast.error("Não foi possível mudar a etapa", { description: errMsg(err, "Tente de novo.") });
    }
  };

  const remove = async () => {
    try {
      await salesService.remove(lead.id);
      await refresh();
      onClose();
    } catch (err) {
      toast.error("Não foi possível excluir", { description: errMsg(err, "Tente de novo.") });
    }
  };

  // Vendas → Planilha: manda (ou manda de novo, já corrigido) só este.
  const sendToSheet = async () => {
    if (!(await save())) return;
    setSendingSheet(true);
    setSaveError(null);
    try {
      await salesService.sendToSheet(lead.id);
      await refresh();
    } catch (err) {
      setSaveError(errMsg(err, "Não foi possível enviar para a planilha."));
    } finally {
      setSendingSheet(false);
    }
  };

  // CEP → endereço (ViaCEP), só preenche o que estiver vazio.
  const lookupCep = async () => {
    const cep = digits(data.cep);
    if (cep.length !== 8) return;
    try {
      const res = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const j = (await res.json()) as { logradouro?: string; bairro?: string; localidade?: string; uf?: string; erro?: boolean };
      if (j.erro) return;
      setData((p) => ({
        ...p,
        street: p.street || (j.logradouro ?? "").toUpperCase(),
        neighborhood: p.neighborhood || (j.bairro ?? "").toUpperCase(),
        city: p.city || (j.localidade ?? "").toUpperCase(),
        state: p.state || (j.uf ?? ""),
      }));
    } catch {
      // sem internet/ViaCEP fora — o vendedor digita
    }
  };

  const startCreateUser = async () => {
    if (!data.name.trim()) {
      setSaveError("Informe o nome antes de criar o usuário.");
      return;
    }
    if (!data.email.trim() && !digits(data.cpfCnpj)) {
      setSaveError("Informe o e-mail ou o CPF/CNPJ (vira o login do cliente).");
      return;
    }
    if (!data.phone1.trim()) {
      setSaveError("Informe o telefone (celular do cliente).");
      return;
    }
    if (!(await save())) return;
    // Congelado na abertura: refetch da lista não pode resetar o dialog.
    setPrefill(leadToPrefill(data));
    setCreateUserOpen(true);
  };

  // O usuário nasce no Traccar; o vínculo com o pré-contrato usa a
  // identidade do GPS Drive, que o próprio dialog já provisionou (dados do
  // Cliente pré-preenchidos) — resolveByTraccarId só garante que existe.
  const handleCreated = async (user: { id: number }) => {
    try {
      await gpsDriveUsersService.resolveByTraccarId(user.id);
      await salesService.linkUser(lead.id, user.id);
      await refresh();
    } catch (err) {
      toast.error("Usuário criado, mas não foi possível ligar ao pré-contrato", {
        description: errMsg(err, "Abra o pré-contrato de novo em alguns segundos."),
      });
    }
  };

  const userCreated = !!lead.user;
  const contractSent = !!lead.contract?.signingToken;
  const signed = !!lead.contract?.signedAt;

  return (
    <>
      <Dialog open onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="max-h-[92vh] max-w-4xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex flex-wrap items-center gap-2">
              {lead.data.name || "Pré-contrato"}
              <StageBadge stage={lead.stage} />
            </DialogTitle>
            <DialogDescription>
              {lead.source === "manual"
                ? "Cadastro manual"
                : lead.source === "sheet"
                  ? "Importado da planilha"
                  : `Formulário “${lead.formTitle ?? "—"}”`}{" "}
              · recebido em{" "}
              {fmtDate(lead.createdAt)}
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-2 md:flex-row">
            <Step n={1} title="Cadastro" done={userCreated} active={!userCreated}>
              {userCreated ? "Completo" : "Confira e complete os dados"}
            </Step>
            <Step n={2} title="Cliente" done={userCreated} active={!userCreated}>
              {lead.user ? `${lead.user.name} · ${lead.user.email}` : "Criar usuário"}
            </Step>
            <Step n={3} title="Contrato" done={signed} active={userCreated && !signed}>
              {signed
                ? `Assinado em ${fmtDate(lead.contract!.signedAt!)}`
                : contractSent
                  ? "Link enviado — aguardando assinatura"
                  : userCreated
                    ? "Gerar contrato"
                    : "Depois de criar o usuário"}
            </Step>
          </div>

          {!userCreated && access.edit && (
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className="text-muted-foreground">Etapa:</span>
              {(["novo", "em_contato", "perdido"] as LeadStatus[]).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setStatus(s)}
                  className={cn(
                    "rounded-full border px-2.5 py-1 font-medium",
                    lead.status === s ? STAGE_STYLES[s] : "border-border text-muted-foreground hover:text-foreground",
                  )}
                >
                  {STAGE_LABELS[s]}
                </button>
              ))}
            </div>
          )}

          <div className="space-y-3">
            <Section title="Dados pessoais">
              <div className="grid gap-3 sm:grid-cols-6">
                <Field className="sm:col-span-4" label="Nome completo / razão social" value={data.name} disabled={readOnly} onChange={(v) => set("name", v.toUpperCase())} />
                <Field className="sm:col-span-2" label="CPF / CNPJ" value={data.cpfCnpj} disabled={readOnly} onChange={(v) => set("cpfCnpj", v)} />
                <Field className="sm:col-span-3" label="E-mail" value={data.email} disabled={readOnly} onChange={(v) => set("email", v.trim().toLowerCase())} />
                <Field className="sm:col-span-1" label="RG" value={data.rg} disabled={readOnly} onChange={(v) => set("rg", v)} />
                <Field className="sm:col-span-2" type="date" label="Nascimento / abertura" value={data.birthDate} disabled={readOnly} onChange={(v) => set("birthDate", v)} />
                <Field className="sm:col-span-3" label="Telefone 1 (celular do cliente)" value={data.phone1} disabled={readOnly} onChange={(v) => set("phone1", v)} />
                <Field className="sm:col-span-3" label="Telefone 2" value={data.phone2} disabled={readOnly} onChange={(v) => set("phone2", v)} />
                <Field
                  className="sm:col-span-3"
                  label="Pessoa autorizada 1"
                  value={data.authorizedPeople[0] ?? ""}
                  disabled={readOnly}
                  onChange={(v) => set("authorizedPeople", [v.toUpperCase(), data.authorizedPeople[1] ?? ""])}
                />
                <Field
                  className="sm:col-span-3"
                  label="Pessoa autorizada 2"
                  value={data.authorizedPeople[1] ?? ""}
                  disabled={readOnly}
                  onChange={(v) => set("authorizedPeople", [data.authorizedPeople[0] ?? "", v.toUpperCase()])}
                />
              </div>
            </Section>

            <Section title="Endereço">
              <div className="grid gap-3 sm:grid-cols-6">
                <Field className="sm:col-span-2" label="CEP" value={data.cep} disabled={readOnly} onChange={(v) => set("cep", v)} onBlur={lookupCep} />
                <Field className="sm:col-span-4" label="Rua / endereço" value={data.street} disabled={readOnly} onChange={(v) => set("street", v.toUpperCase())} />
                <Field className="sm:col-span-1" label="Número" value={data.number} disabled={readOnly} onChange={(v) => set("number", v)} />
                <Field className="sm:col-span-2" label="Complemento" value={data.complement} disabled={readOnly} onChange={(v) => set("complement", v.toUpperCase())} />
                <Field className="sm:col-span-3" label="Bairro" value={data.neighborhood} disabled={readOnly} onChange={(v) => set("neighborhood", v.toUpperCase())} />
                <Field className="sm:col-span-4" label="Cidade" value={data.city} disabled={readOnly} onChange={(v) => set("city", v.toUpperCase())} />
                <Field className="sm:col-span-2" label="UF" value={data.state} disabled={readOnly} onChange={(v) => set("state", v.toUpperCase().slice(0, 2))} />
              </div>
            </Section>

            <Section title="Cobrança">
              {/* Plano e mensalidade ficam em cada veículo (pedido 2026-10-15);
                  aqui só o que vale pro cliente todo. */}
              <div className="grid gap-3 sm:grid-cols-6">
                <Field className="sm:col-span-2" label="Vencimento (dia)" value={data.dueDay} disabled={readOnly} onChange={(v) => set("dueDay", digits(v).slice(0, 2))} />
                <label className="block sm:col-span-4">
                  <span className="mb-1 block text-xs font-medium text-muted-foreground">Forma de pagamento</span>
                  <Input
                    value={data.paymentMethod}
                    disabled={readOnly}
                    list="lead-payment-methods"
                    onChange={(e) => set("paymentMethod", e.target.value)}
                    className="h-9"
                  />
                  <datalist id="lead-payment-methods">
                    {["Boleto", "PIX", "Cartão de crédito", "Cartão de débito", "Débito automático", "Dinheiro"].map((m) => (
                      <option key={m} value={m} />
                    ))}
                  </datalist>
                </label>
              </div>
            </Section>

            <Section title="Veículos — cada um com seu plano e mensalidade">
              <ContractVehiclesEditor
                value={data.vehicles}
                onChange={(v) => set("vehicles", v.map((x) => ({ ...x, monthlyValue: x.monthlyValue ?? null })))}
                registered={[]}
                plans={plans}
                disabled={readOnly}
              />
            </Section>

            <Section title="Observações">
              <textarea
                value={data.notes}
                disabled={readOnly}
                onChange={(e) => set("notes", e.target.value)}
                rows={3}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60"
              />
            </Section>

            {lead.answers.length > 0 && (
              <div className="rounded-lg border border-border">
                <button
                  type="button"
                  onClick={() => setShowAnswers((v) => !v)}
                  className="flex w-full items-center justify-between px-4 py-2.5 text-xs font-bold uppercase tracking-wide text-muted-foreground"
                >
                  Respostas originais do formulário
                  <ChevronDown className={cn("h-4 w-4 transition-transform", showAnswers && "rotate-180")} />
                </button>
                {showAnswers && (
                  <dl className="grid gap-x-6 gap-y-2 border-t border-border px-4 py-3 text-sm sm:grid-cols-2">
                    {lead.answers.map((a) => (
                      <div key={a.fieldId}>
                        <dt className="text-xs text-muted-foreground">{a.label}</dt>
                        <dd className="break-words">{Array.isArray(a.value) ? a.value.join(", ") : a.value}</dd>
                      </div>
                    ))}
                  </dl>
                )}
              </div>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2 border-t border-border pt-4">
            {access.delete &&
              (confirmDelete ? (
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-destructive">Excluir este pré-contrato?</span>
                  <Button size="sm" variant="destructive" onClick={remove}>
                    Excluir
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(false)}>
                    Cancelar
                  </Button>
                </div>
              ) : (
                <Button size="sm" variant="ghost" className="text-muted-foreground hover:text-destructive" onClick={() => setConfirmDelete(true)}>
                  <Trash2 className="mr-1.5 h-4 w-4" /> Excluir
                </Button>
              ))}
            <div className="ml-auto flex flex-wrap items-center gap-2">
              {saveError && <span className="text-xs text-destructive">{saveError}</span>}
              {access.edit && sheetReady && (
                <Button
                  variant="outline"
                  onClick={sendToSheet}
                  disabled={saving || sendingSheet}
                  title={lead.sheetSentAt ? `Enviado em ${fmtDate(lead.sheetSentAt)}` : undefined}
                >
                  {sendingSheet ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : lead.sheetSentAt ? (
                    <CheckCircle2 className="mr-2 h-4 w-4 text-emerald-600" />
                  ) : (
                    <FileSpreadsheet className="mr-2 h-4 w-4 text-emerald-600" />
                  )}
                  {lead.sheetSentAt ? "Reenviar p/ planilha" : "Enviar p/ planilha"}
                </Button>
              )}
              {access.edit && dirty && (
                <Button variant="outline" onClick={save} disabled={saving}>
                  {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Salvar alterações
                </Button>
              )}
              {access.edit && !userCreated && (
                <Button onClick={startCreateUser} disabled={saving}>
                  <UserPlus className="mr-2 h-4 w-4" /> Criar usuário
                </Button>
              )}
              {access.edit && userCreated && (
                <Button onClick={async () => (await save()) && setContractOpen(true)} disabled={saving}>
                  <FileSignature className="mr-2 h-4 w-4" />
                  {contractSent || signed ? "Ver contrato" : "Gerar contrato"}
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {createUserOpen && (
        <CreateUserDialog
          open={createUserOpen}
          onOpenChange={setCreateUserOpen}
          prefill={prefill}
          onCreated={handleCreated}
        />
      )}
      {contractOpen && lead.user && (
        <ContractDialog lead={lead} leadData={data} userId={lead.user.id} onClose={() => setContractOpen(false)} />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Gerar contrato — mesmo ClientContract da aba CONTRATO (Editar Usuário),
// agora com os veículos gravados no próprio contrato.

function ContractDialog({
  lead,
  leadData,
  userId,
  onClose,
}: {
  lead: SalesLead;
  leadData: LeadData;
  userId: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { data: contract, isLoading } = useClientContract(userId);
  const { data: profile } = useClientProfile(userId);
  const { data: templates = [] } = useContractTemplates();
  const { data: signatories = [] } = useContractSignatories();
  const { data: whatsapp } = useWhatsappIntegration();
  // Veículos já cadastrados do cliente (podem ir pro contrato com um clique).
  const { data: clientDevices } = useDevicePermissions(lead.user?.traccarUserId ?? undefined, true);
  const { data: plans } = usePlans();
  const updateContract = useUpdateClientContract();
  const generateLink = useGenerateContractSigningLink();
  const sendWhatsapp = useSendSigningLinkByWhatsapp();

  const [templateId, setTemplateId] = useState("");
  const [signatoryIds, setSignatoryIds] = useState<string[]>([]);
  const [recurrence, setRecurrence] = useState("");
  const [total, setTotal] = useState("");
  const [vehicles, setVehicles] = useState<ContractVehicle[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState(false);
  const previewRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (preview) previewRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [preview]);
  const [initialized, setInitialized] = useState(false);
  const { data: versions = [] } = useClientContractVersions(userId);
  // Contrato anterior aberto na pré-visualização/PDF (null = o atual).
  const [viewing, setViewing] = useState<ClientContractVersion | null>(null);
  const [startingNew, setStartingNew] = useState(false);
  const deleteContract = useDeleteClientContract();
  const [confirmDeleteContract, setConfirmDeleteContract] = useState(false);

  // Veículos do pré-cadastro já com plano/mensalidade (fallback: os do formulário).
  const leadVehicles = (): ContractVehicle[] =>
    leadData.vehicles.map((v) => ({
      ...v,
      plan: v.plan || planName(leadData.plan),
      monthlyValue: v.monthlyValue ?? leadData.planValue,
    }));

  // Mensalidade de cada veículo → recorrência = soma (dá pra ajustar à mão).
  const changeVehicles = (next: ContractVehicle[]) => {
    setVehicles(next);
    const total = vehiclesMonthlyTotal(next);
    if (total != null) setRecurrence(brlInput(total));
  };

  // Contrato já salvo manda; senão vem tudo do pré-cadastro.
  const { isLoading: signatoriesLoading } = useContractSignatories();
  useEffect(() => {
    if (initialized || isLoading || signatoriesLoading) return;
    setTemplateId(contract?.templateId ?? (templates.length === 1 ? templates[0]!.id : ""));
    const saved = contractSignatoryIds(contract);
    setSignatoryIds(saved.length || contract?.signedAt ? saved : defaultSignatoryIds(signatories));
    setRecurrence(
      brlInput(contract?.recurrenceValue ?? vehiclesMonthlyTotal(leadVehicles()) ?? leadData.planValue),
    );
    setTotal(brlInput(contract?.contractValue ?? null));
    setVehicles(contract?.vehicles?.length ? contract.vehicles : leadVehicles());
    setInitialized(true);
  }, [initialized, isLoading, signatoriesLoading, contract, templates, signatories, leadData]);

  const signed = !!contract?.signedAt;

  // "Novo contrato" (pedido 2026-10-15): cliente já assinou e entrou mais um
  // veículo. Guarda o assinado (fica em Contratos anteriores) e reabre com os
  // mesmos dados + os veículos do pré-cadastro que ainda não estavam nele.
  // "Em branco" (pedido 2026-10-19): copiar trazia junto tudo que já tinha
  // sido gerado antes (inclusive valor errado); agora dá pra começar limpo —
  // só modelo e signatários da empresa continuam.
  const startNewContract = async (mode: "copy" | "blank") => {
    setStartingNew(true);
    setError(null);
    try {
      let next = await gpsDriveClientContractService.startNewVersion(userId);
      if (mode === "blank") {
        next = await updateContract.mutateAsync({
          userId,
          data: {
            templateId: next.templateId,
            signatoryId: next.signatoryId,
            signatoryIds: next.signatoryIds,
            recurrenceValue: null,
            contractValue: null,
            vehicles: [],
          },
        });
      }
      qc.setQueryData(queryKeys.clientContract(userId), next);
      await qc.invalidateQueries({ queryKey: [...queryKeys.clientContract(userId), "versions"] });
      await qc.invalidateQueries({ queryKey: SALES_LEADS_KEY });
      if (mode === "blank") {
        setVehicles([]);
        setRecurrence("");
        setTotal("");
        setViewing(null);
        setPreview(false);
        toast.success("Novo contrato aberto em branco", {
          description: "O assinado ficou guardado. Preencha os veículos e valores e gere o link.",
        });
        return;
      }
      const key = (p: string) => p.toUpperCase().replace(/[^A-Z0-9]/g, "");
      const have = new Set(next.vehicles.map((v) => key(v.plate)).filter(Boolean));
      const extra = leadVehicles().filter((v) => (v.plate || v.model) && !(v.plate && have.has(key(v.plate))));
      changeVehicles([...next.vehicles, ...extra]);
      setViewing(null);
      setPreview(false);
      toast.success("Novo contrato aberto", {
        description: "O assinado ficou guardado. Adicione o veículo novo e gere o link.",
      });
    } catch (err) {
      setError(errMsg(err, "Não foi possível abrir um novo contrato."));
    } finally {
      setStartingNew(false);
    }
  };
  const plateKey = (p: string) => p.toUpperCase().replace(/[^A-Z0-9]/g, "");
  const pendingNewVehicles = signed
    ? leadData.vehicles.filter(
        (v) => v.plate && !(contract?.vehicles ?? []).some((c) => plateKey(c.plate) === plateKey(v.plate)),
      ).length
    : 0;
  const linkUrl = contract?.signingToken ? `${window.location.origin}/contrato/assinar/${contract.signingToken}` : null;
  const busy = updateContract.isPending || generateLink.isPending;

  const saveContract = async (withNewLink: boolean) => {
    if (!templateId) {
      setError("Escolha o modelo de contrato.");
      return;
    }
    setError(null);
    try {
      await updateContract.mutateAsync({
        userId,
        data: {
          templateId,
          signatoryId: signatoryIds[0] ?? null,
          signatoryIds,
          recurrenceValue: parseBrl(recurrence),
          contractValue: parseBrl(total),
          vehicles: vehicles.filter((v) => v.plate || v.model),
        },
      });
      if (withNewLink) await generateLink.mutateAsync(userId);
      await qc.invalidateQueries({ queryKey: SALES_LEADS_KEY });
    } catch (err) {
      setError(errMsg(err, "Não foi possível salvar o contrato."));
    }
  };

  // Excluir (2026-10-16): só o atual e só se não assinado. Se veio de "Novo
  // contrato", o assinado anterior volta a ser o atual; a tela recarrega.
  const removeContract = async () => {
    setError(null);
    try {
      await deleteContract.mutateAsync(userId);
      await qc.invalidateQueries({ queryKey: SALES_LEADS_KEY });
      setConfirmDeleteContract(false);
      setViewing(null);
      setPreview(false);
      setInitialized(false);
    } catch (err) {
      setError(errMsg(err, "Não foi possível excluir o contrato."));
    }
  };

  const copyLink = async () => {
    if (!linkUrl) return;
    try {
      await navigator.clipboard.writeText(linkUrl);
      toast.success("Link copiado");
    } catch {
      toast.error("Não foi possível copiar o link");
    }
  };

  const sendLink = async () => {
    if (!linkUrl) return;
    try {
      await sendWhatsapp.mutateAsync({ userId, url: linkUrl });
      toast.success("Link enviado por WhatsApp");
    } catch (err) {
      setError(errMsg(err, "Não foi possível enviar por WhatsApp."));
    }
  };

  const cli = clientProfileToCliState(profile);
  // Pré-visualização (pedido 2026-10-14: "pré-visualizar antes de gerar o
  // contrato pra ver se está tudo certo"): enquanto não assinado, o
  // documento mostra o que está NA TELA agora (modelo, valores, veículos,
  // signatários), salvo ou não. Assinado = sempre o que foi gravado.
  const draft: ClientContract | null = viewing
    ? viewing.contract
    : signed && contract
      ? contract
      : {
          templateId: templateId || null,
          signatoryId: signatoryIds[0] ?? null,
          signatoryIds,
          contractValue: parseBrl(total),
          recurrenceValue: parseBrl(recurrence),
          signedAt: null,
          signedMethod: null,
          signingToken: contract?.signingToken ?? null,
          signedByName: null,
          signedLatitude: null,
          signedLongitude: null,
          signedPhotoKey: null,
          signedPhotoUrl: null,
          vehicles: vehicles.filter((v) => v.plate || v.model),
        };
  const template = viewing
    ? (viewing.template ?? undefined)
    : templates.find((t) => t.id === draft?.templateId);
  const signers = contractSignatoryIds(draft).flatMap((id) => {
    const s = signatories.find((x) => x.id === id);
    return s ? [{ name: s.name, role: s.role, cpfCnpj: s.cpfCnpj }] : [];
  });
  const docProps = draft
    ? {
        clientName: cli.nomeContrato || lead.user?.name || leadData.name,
        cpfCnpj: cli.cpfCnpj || leadData.cpfCnpj,
        phone: cli.celular || leadData.phone1,
        email: lead.user?.email ?? leadData.email,
        address: [
          [cli.endereco, cli.numero].filter(Boolean).join(", "),
          cli.bairro,
          [cli.cidade, cli.estado].filter(Boolean).join("/"),
          cli.cep,
        ]
          .filter(Boolean)
          .join(" — "),
        dueDay: cli.diaVencimento || leadData.dueDay,
        vehicles: [],
        plans: [],
        template: template ? { title: template.title, content: template.content } : null,
        contractValue: brlInput(draft.contractValue),
        recurrenceValue: brlInput(draft.recurrenceValue),
        contract: draft,
        signatory: signers[0] ?? null,
        signatories: signers,
      }
    : null;

  const selectCls =
    "h-9 w-full rounded-md border border-input bg-background px-3 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60";

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSignature className="h-4 w-4" /> Contrato
          </DialogTitle>
          <DialogDescription>{lead.user?.name ?? leadData.name}</DialogDescription>
        </DialogHeader>

        {isLoading || !initialized ? (
          <GpsInlineLoader className="h-40" />
        ) : (
          <div className="space-y-3">
            {signed && (
              <div className="flex flex-wrap items-center gap-2 rounded-lg border border-emerald-600/30 bg-emerald-600/10 px-3 py-2 text-sm">
                <span className="flex items-center gap-2 font-medium text-emerald-700 dark:text-emerald-400">
                  <CheckCircle2 className="h-4 w-4" /> Assinado em {fmtDate(contract!.signedAt!)}
                  {(contract?.version ?? 1) > 1 && <span className="font-normal">· versão {contract!.version}</span>}
                </span>
                <span className="ml-auto flex items-center gap-2">
                  {pendingNewVehicles > 0 && (
                    <span className="text-xs text-amber-700 dark:text-amber-300">
                      {pendingNewVehicles} veículo(s) do cadastro fora deste contrato
                    </span>
                  )}
                  <Button size="sm" onClick={() => startNewContract("blank")} disabled={startingNew}>
                    {startingNew ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Plus className="mr-1.5 h-3.5 w-3.5" />}
                    Novo contrato em branco
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => startNewContract("copy")} disabled={startingNew}>
                    Novo copiando este (veículo novo)
                  </Button>
                </span>
              </div>
            )}
            {!signed && (contract?.version ?? 1) > 1 && (
              <div className="rounded-lg border border-sky-500/30 bg-sky-500/10 px-3 py-2 text-xs text-sky-800 dark:text-sky-300">
                Novo contrato (versão {contract!.version}) — o anterior assinado continua guardado em “Contratos anteriores”.
                Confira os veículos, adicione o novo e gere o link.
              </div>
            )}

            <Section title="Modelo e valores">
              {templates.length === 0 ? (
                <div className="text-sm text-muted-foreground">
                  Nenhum modelo de contrato cadastrado — crie em Administração → Contrato.
                </div>
              ) : (
                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="block">
                    <span className="mb-1 block text-xs font-medium text-muted-foreground">Modelo de contrato</span>
                    <select className={selectCls} value={templateId} disabled={signed} onChange={(e) => setTemplateId(e.target.value)}>
                      <option value="">Selecione…</option>
                      {templates.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.title}
                        </option>
                      ))}
                    </select>
                  </label>
                  <div className="sm:col-span-2">
                    <span className="mb-1 block text-xs font-medium text-muted-foreground">
                      Sócios / testemunhas que assinam pela empresa (pode marcar mais de um)
                    </span>
                    <ContractSignatoryPicker
                      signatories={signatories}
                      value={signatoryIds}
                      onChange={setSignatoryIds}
                      disabled={signed}
                    />
                  </div>
                  <Field
                    label="Valor da recorrência (R$) — soma das mensalidades"
                    value={recurrence}
                    disabled={signed}
                    onChange={setRecurrence}
                    placeholder="0,00"
                  />
                  <Field label="Valor total / adesão (R$)" value={total} disabled={signed} onChange={setTotal} placeholder="0,00" />
                </div>
              )}
            </Section>

            <Section title="Veículos do contrato">
              <p className="mb-2 text-xs text-muted-foreground">
                Não precisa de rastreador vinculado — digite os dados ou escolha um veículo já cadastrado do cliente.
              </p>
              <ContractVehiclesEditor
                value={vehicles}
                onChange={changeVehicles}
                registered={clientDevices ?? []}
                plans={plans}
                disabled={signed}
              />
            </Section>

            {versions.length > 0 && (
              <Section title="Contratos anteriores">
                <div className="space-y-1.5">
                  {versions.map((v) => (
                    <div key={v.id} className="flex flex-wrap items-center gap-2 rounded-md bg-muted/40 px-3 py-2 text-sm">
                      <FileSignature className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">Versão {v.version}</span>
                      <span className="text-muted-foreground">
                        · {v.signedAt ? `assinado em ${fmtDate(v.signedAt)}` : "não assinado"} · {v.contract.vehicles.length}{" "}
                        veículo(s)
                      </span>
                      <Button
                        size="sm"
                        variant={viewing?.id === v.id ? "default" : "outline"}
                        className="ml-auto h-7"
                        onClick={() => {
                          const same = viewing?.id === v.id;
                          setViewing(same ? null : v);
                          setPreview(!same);
                        }}
                      >
                        <Eye className="mr-1.5 h-3.5 w-3.5" /> {viewing?.id === v.id ? "Fechar" : "Ver / PDF"}
                      </Button>
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {linkUrl && (
              <Section title="Link de assinatura">
                <div className="flex flex-wrap items-center gap-2">
                  <code className="min-w-0 flex-1 truncate rounded-md bg-muted px-2 py-1.5 text-xs">{linkUrl}</code>
                  <Button size="sm" variant="outline" onClick={copyLink}>
                    <Copy className="mr-1.5 h-3.5 w-3.5" /> Copiar
                  </Button>
                  <Button size="sm" variant="outline" asChild>
                    <a href={linkUrl} target="_blank" rel="noreferrer">
                      <ExternalLink className="mr-1.5 h-3.5 w-3.5" /> Abrir
                    </a>
                  </Button>
                  {whatsapp?.connected && !signed && (
                    <Button size="sm" variant="outline" onClick={sendLink} disabled={sendWhatsapp.isPending}>
                      {sendWhatsapp.isPending ? (
                        <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <MessageCircle className="mr-1.5 h-3.5 w-3.5" />
                      )}
                      Enviar por WhatsApp
                    </Button>
                  )}
                </div>
              </Section>
            )}

            {preview && docProps && (
              <div
                ref={previewRef}
                className="overflow-hidden rounded-lg border border-border"
              >
                {viewing && (
                  <div className="border-b border-border bg-sky-500/10 px-4 py-2 text-xs text-sky-800 dark:text-sky-300">
                    Contrato anterior — versão {viewing.version}
                    {viewing.signedAt ? `, assinado em ${fmtDate(viewing.signedAt)}` : ""}. “Baixar (PDF)” baixa esta versão.
                  </div>
                )}
                {!signed && !viewing && (
                  <div className="border-b border-border bg-amber-500/10 px-4 py-2 text-xs text-amber-800 dark:text-amber-300">
                    Pré-visualização com os dados desta tela — é assim que o cliente vai ver. Nada foi enviado ainda.
                  </div>
                )}
                <ClientContractDocument {...docProps} className="p-4" />
              </div>
            )}
            {docProps && <ClientContractPrintReport open {...docProps} />}

            <div className="flex flex-wrap items-center gap-2 border-t border-border pt-4">
              {docProps && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (preview) setViewing(null);
                      setPreview((v) => !v);
                    }}
                  >
                    <Eye className="mr-1.5 h-4 w-4" />
                    {preview ? "Esconder pré-visualização" : signed ? "Ver contrato" : "Pré-visualizar contrato"}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => printClientContract(`Contrato — ${lead.user?.name ?? leadData.name}`)}>
                    <Download className="mr-1.5 h-4 w-4" /> Baixar (PDF)
                  </Button>
                </>
              )}
              {!signed && contract &&
                (confirmDeleteContract ? (
                  <div className="flex items-center gap-2 text-xs">
                    <span className="text-destructive">
                      {(contract.version ?? 1) > 1
                        ? "Excluir este contrato? O assinado anterior volta a valer."
                        : "Excluir este contrato?"}
                    </span>
                    <Button size="sm" variant="destructive" onClick={removeContract} disabled={deleteContract.isPending}>
                      {deleteContract.isPending ? "Excluindo…" : "Excluir"}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setConfirmDeleteContract(false)}>
                      Cancelar
                    </Button>
                  </div>
                ) : (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-muted-foreground hover:text-destructive"
                    onClick={() => setConfirmDeleteContract(true)}
                  >
                    <Trash2 className="mr-1.5 h-4 w-4" /> Excluir contrato
                  </Button>
                ))}
              <div className="ml-auto flex flex-wrap items-center gap-2">
                {error && <span className="text-xs text-destructive">{error}</span>}
                {!signed && templates.length > 0 && (
                  <>
                    {linkUrl ? (
                      <>
                        <Button variant="outline" onClick={() => saveContract(false)} disabled={busy}>
                          Salvar
                        </Button>
                        <Button variant="outline" onClick={() => saveContract(true)} disabled={busy}>
                          <Link2 className="mr-2 h-4 w-4" /> Gerar novo link
                        </Button>
                      </>
                    ) : (
                      <Button onClick={() => saveContract(true)} disabled={busy}>
                        {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Link2 className="mr-2 h-4 w-4" />}
                        Salvar e gerar link de assinatura
                      </Button>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
